import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { socialProofAPI } from '../lib/api';
import {
  Star, Video, Images, Award, Plus, Pencil, Trash2, Search, ExternalLink,
  FolderOpen, Loader2, CheckCircle2, Circle, Upload, Link2, ShieldCheck,
} from 'lucide-react';

const TYPES = {
  resena: { label: 'Reseña / Valoración', icon: Star, color: 'bg-amber-100 text-amber-800' },
  video: { label: 'Testimonio en vídeo', icon: Video, color: 'bg-rose-100 text-rose-800' },
  caso: { label: 'Caso de éxito / Antes y después', icon: Images, color: 'bg-sky-100 text-sky-800' },
  dato: { label: 'Dato de confianza', icon: Award, color: 'bg-violet-100 text-violet-800' },
};
const ORIGENES = ['Google', 'Instagram', 'Facebook', 'WhatsApp', 'Otro'];

const emptyForm = () => ({
  tipo: 'resena', estado_uso: 'sin_usar', usado_donde: '', usado_fecha: '',
  data: { texto: '', autor: '', fecha: '', puntuacion: 5, origen: 'Google', captura: '',
    video_url: '', video_file: '', nombre_cliente: '', descripcion: '',
    foto_antes: '', foto_despues: '', titulo: '', explicacion: '',
    dato: '', contexto: '' },
});

export default function SocialProofSection({ clientId }) {
  const [items, setItems] = useState([]);
  const [driveLink, setDriveLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fTipo, setFTipo] = useState('all');
  const [fEstado, setFEstado] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveDraft, setDriveDraft] = useState('');
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await socialProofAPI.list(clientId);
      setItems(Array.isArray(res?.items) ? res.items : []);
      setDriveLink(res?.drive_link || '');
    } catch { toast.error('Error cargando la prueba social'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setData = (k, v) => setForm((p) => ({ ...p, data: { ...p.data, [k]: v } }));

  const openCreate = () => { setForm(emptyForm()); setEditingId(null); setDialogOpen(true); };
  const openEdit = (it) => {
    setForm({ tipo: it.tipo, estado_uso: it.estado_uso || 'sin_usar', usado_donde: it.usado_donde || '', usado_fecha: it.usado_fecha || '', data: { ...emptyForm().data, ...(it.data || {}) } });
    setEditingId(it.id); setDialogOpen(true);
  };

  const uploadImg = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const res = await socialProofAPI.uploadMedia(clientId, file);
      setData(key, res.filename);
    } catch (e) { toast.error(e.message || 'Error subiendo imagen'); }
    finally { setUploading(''); }
  };

  const handleSave = async () => {
    const d = form.data;
    if (form.tipo === 'resena' && !d.texto.trim()) { toast.error('Añade el texto de la reseña'); return; }
    if (form.tipo === 'video' && !d.video_url.trim() && !d.video_file) { toast.error('Añade el vídeo o su enlace'); return; }
    if (form.tipo === 'caso' && !d.titulo.trim()) { toast.error('Añade el título del caso'); return; }
    if (form.tipo === 'dato' && !d.dato.trim()) { toast.error('Añade el dato de confianza'); return; }
    setSaving(true);
    try {
      const payload = { tipo: form.tipo, data: form.data, estado_uso: form.estado_uso, usado_donde: form.usado_donde, usado_fecha: form.usado_fecha };
      if (editingId) { await socialProofAPI.update(editingId, payload); toast.success('Actualizado'); }
      else { await socialProofAPI.create(clientId, payload); toast.success('Prueba social añadida'); }
      setDialogOpen(false); await load();
    } catch (e) { toast.error(e.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (it) => {
    if (!window.confirm('¿Eliminar este elemento de prueba social?')) return;
    try { await socialProofAPI.delete(it.id); toast.success('Eliminado'); await load(); }
    catch { toast.error('No se pudo eliminar'); }
  };

  const toggleUsed = async (it) => {
    const next = it.estado_uso === 'usado' ? 'sin_usar' : 'usado';
    try {
      await socialProofAPI.update(it.id, { estado_uso: next, usado_fecha: next === 'usado' ? new Date().toISOString().slice(0, 10) : '' });
      await load();
    } catch { toast.error('No se pudo actualizar el estado'); }
  };

  const saveDrive = async () => {
    try { const res = await socialProofAPI.setDriveLink(clientId, driveDraft.trim()); setDriveLink(res.drive_link); setDriveOpen(false); toast.success('Enlace de Drive guardado'); }
    catch { toast.error('No se pudo guardar el enlace'); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (fTipo !== 'all' && it.tipo !== fTipo) return false;
      if (fEstado !== 'all' && (it.estado_uso || 'sin_usar') !== fEstado) return false;
      if (!q) return true;
      const blob = JSON.stringify(it.data || {}).toLowerCase() + (it.usado_donde || '').toLowerCase();
      return blob.includes(q);
    });
  }, [items, search, fTipo, fEstado]);

  const sinUsar = items.filter((i) => (i.estado_uso || 'sin_usar') === 'sin_usar').length;

  const Img = ({ file, alt }) => file ? (
    <img src={socialProofAPI.mediaUrl(file)} alt={alt} className="rounded-md object-cover w-full h-32 border" />
  ) : null;

  const FileField = ({ label, k }) => (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input ref={(el) => (fileRefs.current[k] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(k, e.target.files?.[0])} data-testid={`sp-file-${k}`} />
        <Button type="button" variant="outline" size="sm" onClick={() => fileRefs.current[k]?.click()} disabled={uploading === k}>
          {uploading === k ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Subir imagen
        </Button>
        {form.data[k] && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Adjunta</span>}
      </div>
      {form.data[k] && <img src={socialProofAPI.mediaUrl(form.data[k])} alt="preview" className="mt-2 rounded-md border max-h-32" />}
    </div>
  );

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <Card data-testid="social-proof-section">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Prueba Social</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Biblioteca de material de confianza del cliente. {sinUsar > 0 && <span className="text-emerald-700 font-medium">{sinUsar} sin usar disponibles.</span>}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {driveLink ? (
              <a href={driveLink} target="_blank" rel="noreferrer" data-testid="sp-drive-link">
                <Button variant="outline" size="sm"><FolderOpen className="w-4 h-4 mr-1" /> Carpeta Drive <ExternalLink className="w-3 h-3 ml-1" /></Button>
              </a>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => { setDriveDraft(driveLink); setDriveOpen(true); }} data-testid="sp-drive-edit"><Link2 className="w-4 h-4 mr-1" /> {driveLink ? 'Editar Drive' : 'Enlace Drive'}</Button>
            <Button size="sm" onClick={openCreate} data-testid="sp-new-btn"><Plus className="w-4 h-4 mr-1" /> Añadir prueba social</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar…" className="pl-9" data-testid="sp-search" />
          </div>
          <Select value={fTipo} onValueChange={setFTipo}>
            <SelectTrigger className="w-[190px]" data-testid="sp-filter-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fEstado} onValueChange={setFEstado}>
            <SelectTrigger className="w-[150px]" data-testid="sp-filter-estado"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="sin_usar">Sin usar</SelectItem>
              <SelectItem value="usado">Usado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="sp-empty">
            {items.length === 0 ? 'Aún no hay prueba social. Añade reseñas, testimonios, casos de éxito o datos de confianza.' : 'Ningún elemento coincide con los filtros.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((it) => {
              const T = TYPES[it.tipo] || TYPES.resena; const Icon = T.icon; const d = it.data || {};
              const used = (it.estado_uso || 'sin_usar') === 'usado';
              return (
                <div key={it.id} className="rounded-lg border p-4 space-y-2" data-testid={`sp-card-${it.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <Badge className={`${T.color} gap-1 text-[10px]`}><Icon className="w-3 h-3" /> {T.label}</Badge>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleUsed(it)} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${used ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`sp-toggle-${it.id}`}>
                        {used ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />} {used ? 'Usado' : 'Sin usar'}
                      </button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(it)} data-testid={`sp-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(it)} data-testid={`sp-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>

                  {it.tipo === 'resena' && (<>
                    <div className="flex items-center gap-1">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < (d.puntuacion || 0) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />)}</div>
                    <p className="text-sm italic">“{d.texto}”</p>
                    <p className="text-xs text-muted-foreground">{d.autor}{d.origen ? ` · ${d.origen}` : ''}{d.fecha ? ` · ${d.fecha}` : ''}</p>
                    <Img file={d.captura} alt="captura" />
                  </>)}
                  {it.tipo === 'video' && (<>
                    <p className="text-sm font-medium">{d.nombre_cliente}</p>
                    <p className="text-xs text-muted-foreground">{d.descripcion}</p>
                    {d.video_url && <a href={d.video_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1"><Video className="w-3 h-3" /> Ver vídeo</a>}
                    {d.video_file && <a href={socialProofAPI.mediaUrl(d.video_file)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 ml-2"><Video className="w-3 h-3" /> Vídeo subido</a>}
                  </>)}
                  {it.tipo === 'caso' && (<>
                    <p className="text-sm font-medium">{d.titulo}</p>
                    <p className="text-xs text-muted-foreground">{d.explicacion}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div><p className="text-[10px] text-muted-foreground mb-1">Antes</p><Img file={d.foto_antes} alt="antes" /></div>
                      <div><p className="text-[10px] text-muted-foreground mb-1">Después</p><Img file={d.foto_despues} alt="despues" /></div>
                    </div>
                  </>)}
                  {it.tipo === 'dato' && (<>
                    <p className="text-base font-semibold text-primary">{d.dato}</p>
                    <p className="text-xs text-muted-foreground">{d.contexto}</p>
                  </>)}

                  {used && (it.usado_donde || it.usado_fecha) && (
                    <p className="text-[11px] text-slate-500 border-t pt-2">Usado en: {it.usado_donde || '—'}{it.usado_fecha ? ` (${it.usado_fecha})` : ''}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="sp-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar prueba social' : 'Añadir prueba social'}</DialogTitle>
            <DialogDescription>Etiqueta el material de confianza del cliente para reutilizarlo en contenido y anuncios.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((p) => ({ ...p, tipo: v }))} disabled={!!editingId}>
                <SelectTrigger data-testid="sp-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {form.tipo === 'resena' && (<>
              <div><Label>Texto de la reseña</Label><Textarea rows={3} value={form.data.texto} onChange={(e) => setData('texto', e.target.value)} data-testid="sp-texto" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Autor</Label><Input value={form.data.autor} onChange={(e) => setData('autor', e.target.value)} data-testid="sp-autor" /></div>
                <div><Label>Fecha</Label><Input type="date" value={form.data.fecha} onChange={(e) => setData('fecha', e.target.value)} data-testid="sp-fecha" /></div>
                <div>
                  <Label>Puntuación</Label>
                  <Select value={String(form.data.puntuacion)} onValueChange={(v) => setData('puntuacion', Number(v))}>
                    <SelectTrigger data-testid="sp-puntuacion"><SelectValue /></SelectTrigger>
                    <SelectContent>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Origen</Label>
                  <Select value={form.data.origen} onValueChange={(v) => setData('origen', v)}>
                    <SelectTrigger data-testid="sp-origen"><SelectValue /></SelectTrigger>
                    <SelectContent>{ORIGENES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <FileField label="Captura de pantalla (opcional)" k="captura" />
            </>)}

            {form.tipo === 'video' && (<>
              <div><Label>Enlace al vídeo</Label><Input value={form.data.video_url} onChange={(e) => setData('video_url', e.target.value)} placeholder="https://…" data-testid="sp-video-url" /></div>
              <FileField label="…o subir vídeo (opcional, mejor enlace si es pesado)" k="video_file" />
              <div><Label>Nombre del cliente que habla</Label><Input value={form.data.nombre_cliente} onChange={(e) => setData('nombre_cliente', e.target.value)} data-testid="sp-nombre-cliente" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fecha</Label><Input type="date" value={form.data.fecha} onChange={(e) => setData('fecha', e.target.value)} /></div>
              </div>
              <div><Label>Breve descripción</Label><Textarea rows={2} value={form.data.descripcion} onChange={(e) => setData('descripcion', e.target.value)} data-testid="sp-descripcion" /></div>
            </>)}

            {form.tipo === 'caso' && (<>
              <div><Label>Título del caso</Label><Input value={form.data.titulo} onChange={(e) => setData('titulo', e.target.value)} data-testid="sp-titulo" /></div>
              <div><Label>Explicación</Label><Textarea rows={2} value={form.data.explicacion} onChange={(e) => setData('explicacion', e.target.value)} data-testid="sp-explicacion" /></div>
              <div className="grid grid-cols-2 gap-3">
                <FileField label="Foto antes" k="foto_antes" />
                <FileField label="Foto después" k="foto_despues" />
              </div>
              <div><Label>Fecha</Label><Input type="date" value={form.data.fecha} onChange={(e) => setData('fecha', e.target.value)} /></div>
            </>)}

            {form.tipo === 'dato' && (<>
              <div><Label>Dato o frase de confianza</Label><Input value={form.data.dato} onChange={(e) => setData('dato', e.target.value)} placeholder="Ej. Más de 2.000 clientes" data-testid="sp-dato" /></div>
              <div><Label>Contexto o fuente</Label><Textarea rows={2} value={form.data.contexto} onChange={(e) => setData('contexto', e.target.value)} data-testid="sp-contexto" /></div>
            </>)}

            <div className="rounded-lg border p-3 space-y-3">
              <div>
                <Label>Estado de uso</Label>
                <Select value={form.estado_uso} onValueChange={(v) => setForm((p) => ({ ...p, estado_uso: v }))}>
                  <SelectTrigger data-testid="sp-estado-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="sin_usar">Sin usar</SelectItem><SelectItem value="usado">Usado</SelectItem></SelectContent>
                </Select>
              </div>
              {form.estado_uso === 'usado' && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>¿Dónde se usó?</Label><Input value={form.usado_donde} onChange={(e) => setForm((p) => ({ ...p, usado_donde: e.target.value }))} placeholder="Publicación/campaña" data-testid="sp-usado-donde" /></div>
                  <div><Label>Fecha de uso</Label><Input type="date" value={form.usado_fecha} onChange={(e) => setForm((p) => ({ ...p, usado_fecha: e.target.value }))} data-testid="sp-usado-fecha" /></div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="sp-save-btn">{saving ? 'Guardando…' : (editingId ? 'Guardar' : 'Añadir')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drive link dialog */}
      <Dialog open={driveOpen} onOpenChange={setDriveOpen}>
        <DialogContent className="max-w-md" data-testid="sp-drive-dialog">
          <DialogHeader>
            <DialogTitle>Carpeta "Prueba Social" en Google Drive</DialogTitle>
            <DialogDescription>Pega el enlace a la carpeta de Drive donde vive el material pesado de este cliente.</DialogDescription>
          </DialogHeader>
          <Input value={driveDraft} onChange={(e) => setDriveDraft(e.target.value)} placeholder="https://drive.google.com/…" data-testid="sp-drive-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDriveOpen(false)}>Cancelar</Button>
            <Button onClick={saveDrive} data-testid="sp-drive-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
